import { derived, writable } from "svelte/store";
import { localUserStore } from "../Connection/LocalUserStore";
import {
    getCaptureNoiseReductionProvider,
    type NoiseReductionProvider,
    type NoiseReductionStatus,
} from "./NoiseReductionPolicy";
export type { NoiseReductionProvider, NoiseReductionStatus } from "./NoiseReductionPolicy";

export interface NoiseReductionState {
    provider: NoiseReductionProvider;
    effectiveProvider: NoiseReductionProvider;
    status: NoiseReductionStatus;
    fallbackReason: string | null;
}

function createNoiseReductionStore() {
    const initialProvider = localUserStore.getNoiseReductionProvider();

    let currentState: NoiseReductionState = {
        provider: initialProvider,
        effectiveProvider: initialProvider,
        status: "idle",
        fallbackReason: null,
    };

    const { subscribe, set } = writable<NoiseReductionState>(currentState);

    const setState = (nextState: NoiseReductionState): void => {
        if (
            currentState.provider === nextState.provider &&
            currentState.effectiveProvider === nextState.effectiveProvider &&
            currentState.status === nextState.status &&
            currentState.fallbackReason === nextState.fallbackReason
        ) {
            return;
        }

        currentState = nextState;
        set(currentState);
    };

    return {
        subscribe,

        setProvider(provider: NoiseReductionProvider): void {
            localUserStore.setNoiseReductionProvider(provider);
            const isAlreadyEffective = currentState.effectiveProvider === provider;
            setState({
                ...currentState,
                provider,
                status: isAlreadyEffective ? "active" : "idle",
                fallbackReason: null,
            });
        },

        setStatus(status: NoiseReductionStatus): void {
            setState({ ...currentState, status });
        },

        setFallback(reason: string): void {
            setState({
                ...currentState,
                effectiveProvider: "browser",
                status: "fallback",
                fallbackReason: reason,
            });
        },

        setUnavailable(reason: string): void {
            setState({
                ...currentState,
                status: "unavailable",
                fallbackReason: reason,
            });
        },

        setActive(effectiveProvider: NoiseReductionProvider): void {
            setState({
                ...currentState,
                effectiveProvider,
                status: "active",
                fallbackReason: null,
            });
        },

        reset(): void {
            currentState = {
                provider: "browser",
                effectiveProvider: "browser",
                status: "idle",
                fallbackReason: null,
            };
            localUserStore.setNoiseReductionProvider("browser");
            set(currentState);
        },
    };
}

export const noiseReductionStore = createNoiseReductionStore();
export const selectedNoiseReductionProviderStore = derived(noiseReductionStore, ($noiseReductionStore) => {
    return $noiseReductionStore.provider;
});
export const effectiveNoiseReductionProviderStore = derived(noiseReductionStore, ($noiseReductionStore) => {
    return $noiseReductionStore.effectiveProvider;
});
export const captureNoiseReductionProviderStore = derived(noiseReductionStore, ($noiseReductionStore) => {
    return getCaptureNoiseReductionProvider($noiseReductionStore);
});
